/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import com.ionspin.kotlin.crypto.util.encodeToUByteArray
import kotlinx.serialization.Serializable
import net.sergeych.bipack.BipackEncoder
import net.sergeych.bipack.Unsigned
import net.sergeych.synctools.ProtectedOp
import net.sergeych.synctools.invoke

object PBKD {

    /**
     * Parameters to generate a reasonably secure data bytes with [deriveUBytes] or a [SymmetricKey]
     * with [deriveKey]
     */
    @Serializable
    data class Params(
        val kdf: KDF,
        @Unsigned
        val startOffset: Int,
        @Unsigned
        val length: Int,
    ) {

        /**
         * Derive UByteArray from the password in the idempotent way.
         * Could be used to construct keys, IVs, tokens, whatever.
         * Most often [deriveKey] is used instead which derives the key.
         */
        fun deriveUBytes(password: String): UByteArray {
            val bytes = generate(kdf, password)
            return bytes.sliceArray(startOffset..<startOffset + length)
        }

        /**
         * Derive a new key from the password in the idempotent manner.
         * If the [keyId] is provided, it will be used to determine key type (otherwise default asymmetric)
         * and to check the result, and the exception will be thrown.
         *
         * Note that it is reasonably effective to use with linked keys restoring (keys based on the same password).
         * It can also be called repeatedly.
         *
         * @param password to derive from
         * @param keyId optional key id, to get a key type from and check the result
         * @throws IncorrectPasswordException if [keyId] is provided and does not match the derivation result
         */
        fun deriveKey(password: String, keyId: KeyId? = null): SymmetricKey {
            // check key id sanity
            keyId?.let {
                if (it.id.magic != KeysmagicNumber.defaultSymmetric.ordinal) {
                    throw NotSupportedException("deriving key of type ${it.id.magic} is not implemented")
                }
            }
            // this will not be ok when we support other key types:
            if (SymmetricKey.keyLength != length)
                throw NotSupportedException("can't create key from $length byte(s), required ${SymmetricKey.keyLength}")

            // derive
            val key = SymmetricKey(deriveUBytes(password),this)

            // check if we can
            if (keyId != null && keyId != key.id)
                throw IncorrectPasswordException()

            return key
        }
    }

    /**
     * Derive several keys-suitable params
     */
    fun deriveMultipleKeys(password: String,keysCount: Int,
                           complexity: KDF.Complexity = KDF.Complexity.Moderate,
                           salt: UByteArray = KDF.Argon.randomSalt()
    ): List<SymmetricKey> {
        require(keysCount > 0) { "keysCount must be positive" }
        val keyLength = SymmetricKey.keyLength
        val totalSize = keysCount * keyLength
        check(salt.size >= KDF.Argon.saltSize)
        val salt2 = if( salt.size > KDF.Argon.saltSize) salt.sliceArray( 0 ..< KDF.Argon.saltSize) else salt
        val kdf = KDF.Argon.create(complexity, salt2, totalSize)
        return (0 ..< keysCount).map { Params(kdf, it*keyLength, keyLength).deriveKey(password) }
    }

    private val op = ProtectedOp()

    private class Entry(val kdf: KDF) {
        val op = ProtectedOp()
        var data: UByteArray? = null
    }

    /**
     * Binary id is a binary hash-capable key, we use it. byte array can't be a hash key in kotlin: its value
     * does not depend on the byte content. It is unsafe to use it outside the generator!
     */
    private fun key(kdf: KDF, password: String): BinaryId =
        BinaryId.createFromBytes(
            0,
            BipackEncoder.encode(kdf) + blake2b3l(password.encodeToUByteArray()).asByteArray()
        )

    private val cache = HashMap<BinaryId, Entry>()

    /**
     * Get a plain bytes by the KDF parameters specified. It implements short-term caching of
     * KDF results this could be called repeatedly.
     *
     * When called from different threads, allows deriving in parallel, but this could deplete memory
     * or CPU cores.
     */
    fun generate(kdf: KDF, password: String): UByteArray {
        val entry = op.invoke {
            cache.getOrPut(key(kdf, password)) { Entry(kdf) }
        }
        return entry.op {
            if (entry.data == null) {
                entry.data = entry.kdf.deriveKey(password)
            }
            entry.data!!
        }
    }

    /**
     * Generated cache bytes are cached and present in memory probably longer than needed.
     * After this call, the password derivation cache is clear and the new deriving will
     * be performed.
     */
    fun clearCache() {
        op.invoke { cache.clear() }
    }
}



